import "./style.css"
import Color from "./Color"
import { allPalettes, allPalettesWithBlack, randomPaletteWithBlack } from "./randomPalette"
import spectral from "spectral.js"
import weightedRandom from "./weightedRandom"
import Prando from "prando"
import { createNoise3D } from "simplex-noise"
import { TAU } from "./constants"
import config from "./config"
import React from "react"
import { createRoot } from "react-dom/client"
import loadImage from "./loadImage"


/**
 * @type CanvasRenderingContext2D
 */
let ctx;
let canvas;

/**
 * @type CanvasRenderingContext2D
 */
let tmpCtx;
let tmpCanvas;
let random
let noise

const positions = [
    [  0, 0],
    [  1, 0],
    [1/3, 1/3],
    [2/3, 1/3],
    [1/3, 2/3],
    [2/3, 2/3],
    [  0, 1],
    [  1, 1],

    [ 0, 0.5],
    [ 1, 0.5],
    [ 0.5, 0],
    [ 0.5, 1],
    [ 0.5, 0.5]
]


function toScreen(pos)
{
    const { width, height } = config

    return [
        Math.floor(pos[0] * width),
        Math.floor(pos[1] * height),
    ]
}


function isInHistory(history, posA, posB)
{
    for (let i = 0; i < history.length; i++)
    {
        const [a,b] = history[i]
        if (
            (
                posA[0] === a[0] && posA[1] === a[1] &&
                posB[0] === b[0] && posB[1] === b[1]
            ) ||
            (
                posA[0] === b[0] && posA[1] === b[1] &&
                posB[0] === a[0] && posB[1] === a[1]
            )
        )
        {
            return true
        }
    }
    return false
}

function randomPair(palette)
{
    const colA = palette[0|random.next() * palette.length]
    let colB
    do
    {
        colB = palette[0|random.next() * palette.length]
    } while (colA === colB)

    return [ colA, colB]
}


function randomPositionPair(history)
{
    let posA
    let posB
    do
    {
        posA = positions[0 | random.next() * positions.length]
        posB = positions[0 | random.next() * positions.length]

    } while (posA === posB || isInHistory(history, posA, posB))

    let result = [toScreen(posA), toScreen(posB)]
    history.push(result)
    return result
}

function distance(p0, p1)
{
    const [x, y] = p0

    const dx = p1[0] - x
    const dy = p1[1] - y

    return Math.sqrt(dx * dx + dy * dy)
}

function circleShape(ctx, p0,p1)
{
    const [x, y] = p0
    const r = distance(p0, p1)

    ctx.moveTo(x + r, y)
    ctx.arc(x, y, r, 0, TAU, true)

    return true
}

function lineShape(ctx, p0,p1)
{
    const [x0, y0] = p0
    const [x1, y1] = p1

    const dx = x1 - x0
    const dy = y1 - y0

    const d = Math.sqrt(dx * dx + dy * dy)
    const angle = Math.atan2(dy, dx)

    const lineWidth = Math.max(1, Math.floor(d * (0.02 + Math.pow(random.next(), 2) * 0.03)))

    const pt0 = (x0,y0,angle,d) => ctx.moveTo(x0 + Math.cos(angle) * d, y0 + Math.sin(angle) * d )
    const pt1 = (x0,y0,angle,d) => ctx.lineTo(x0 + Math.cos(angle) * d, y0 + Math.sin(angle) * d )

    const a45 = TAU/8

    const dw = Math.sqrt(lineWidth*lineWidth/2)

    pt0(x0,y0,angle - a45, dw)
    pt1(x0,y0,angle + a45, dw)
    pt1(x1,y1,angle + TAU/2 - a45, dw)
    pt1(x1,y1,angle + TAU/2 + a45, dw)
    pt1(x0,y0,angle - a45, dw)

    return false
}

function squareShape(ctx, p0,p1)
{
    const [x0, y0] = p0
    const [x1, y1] = p1

    const dx = x1 - x0
    const dy = y1 - y0

    const d = Math.sqrt(dx * dx + dy * dy)
    const angle = Math.atan2(dy, dx)

    ctx.moveTo(x0 + Math.cos(angle) * d, y0 + Math.sin(angle) * d )
    ctx.lineTo(x0 + Math.cos(angle + TAU/4) * d, y0 + Math.sin(angle + TAU/4) * d )
    ctx.lineTo(x0 + Math.cos(angle + TAU/2) * d, y0 + Math.sin(angle + TAU/2) * d )
    ctx.lineTo(x0 + Math.cos(angle + 3*TAU/4) * d, y0 + Math.sin(angle + 3*TAU/4) * d )
    ctx.lineTo(x0 + Math.cos(angle) * d, y0 + Math.sin(angle) * d )
    return true
}

let marked = []

function randomShape(ctx, p0,p1, canBeMarked)
{
    const rnd = random.next()
    
    const isMarkedShaped = _randomShape(ctx, rnd, p0,p1)
    if (isMarkedShaped && canBeMarked && random.next() < 0.5)
    {
        marked.push([ctx, rnd, p0, p1])
    }

    return isMarkedShaped
}

function mark()
{
    const { width, height} = config

    ctx.lineWidth = Math.floor(Math.sqrt(width * width + height * height) * 0.00046)
    ctx.strokeStyle = "rgba(0,0,0,0.85)"
    ctx.beginPath()
    for (let i = 0; i < marked.length; i++)
    {
        const [ctx, rnd, p0, p1] = marked[i]
        _randomShape(ctx, rnd, p0,p1)
    }
    ctx.stroke()
    marked = []
}


const _randomShape = weightedRandom([
    4, circleShape,
    2, squareShape,
    1, lineShape
])

function paintLayer(ctx, isMarkedLayer)
{
    const history = []
    ctx.beginPath()

    const count = Math.round(2 + random.next() * 2)
    for (let i = 0; i < count; i++)
    {
        const [p0, p1] = randomPositionPair(history)
        randomShape(ctx, p0,p1, isMarkedLayer)
    }
}


function generateFill(colA, colB)
{
    const { width, height} = config

    const choice = Math.floor(4 * random.next())
    let x0,y0,x1,y1
    switch (choice)
    {
        default:
            x0 = 0
            y0 = 0
            x1 = 0
            y1 = height
            break
        case 1:
            x0 = 0
            y0 = 0
            x1 = width
            y1 = 0
            break
        case 2:
            x0 = 0
            y0 = height
            x1 = 0
            y1 = 0
            break
        case 3:
            x0 = width
            y0 = 0
            x1 = 0
            y1 = 0
            break
    }

    const len = 256
    const spread = spectral.palette(
        Color.from(colA).toRGBHex(),
        Color.from(colB).toRGBHex(),
        len,
        spectral.HEX
    )

    const gradient = ctx.createLinearGradient(x0,y0,x1,y1);
    const delta = 1/len
    let a = 0
    for (let i = 0; i < len; i++)
    {
        const col = spread[i]
        gradient.addColorStop(i * delta, Color.from(col).toRGBA(Math.sqrt(a)))
        a += delta
    }
    return gradient
}

function randomSeed()
{
    return Math.floor((Math.random() - 0.5) * 4294967295)
}


let root
let seed
let paletteIndex = -1
let paletteLocked = false

function tempContext(width, height)
{
    const canvas = document.createElement("canvas")
    canvas.width = width
    canvas.height = height
    return canvas.getContext("2d")
}


function scaleToScreen(img)
{
    const {width, height} = config
    const tmp = tempContext(width,height)

    const {canvas} = tmp

    const iRatio = img.width/img.height
    const targetRatio = canvas.width/canvas.height


    let scale
    if (iRatio < targetRatio)
    {
        scale = img.width/canvas.width
    }
    else
    {
        scale = img.height/canvas.height
    }

    const w0 = img.width / scale
    const h0 = img.height / scale

    console.log({w0,h0})

    tmp.drawImage(img, 0,0,w0,h0)
    return canvas
}

function applyPaper()
{
    const { width, height } = config



    const src = ctx.getImageData(0,0,width,height)
    const dst = ctx.getImageData(0,0,width,height)
    const { data : dataSrc } = src
    const { data : dataDst } = dst

    const ns = 0.08 + Math.random() * 0.02

    const nsy = ns*2.7

    const nz0 = Math.random() * ns
    const nz1 = Math.random() * ns
    const nz2 = Math.random() * ns

    const e = 1

    let off = 0

    console.log({nz0,nz1,nz2})

    for (let y = 0; y < height; y++)
    {
        for (let x = 0; x < width; x++)
        {
            const x2 = x + e
            const y2 = y
            const x3 = x
            const y3 = y + e

            const z  = noise( x * ns,  y * nsy, nz0)
            const z2 = noise(x2 * ns, y2 * nsy, nz1)
            const z3 = noise(x3 * ns, y3 * nsy, nz2)

            const a1 = (x2 - x)
            const a2 = (y2 - y)
            const a3 = (z2 - z)
            const b1 = (x3 - x)
            const b2 = (y3 - y)
            const b3 = (z3 - z)

            const nx = a2*b3-a3*b2
            const ny = a3*b1-a1*b3
            const nz = a1*b2-a2*b1

            const lx = 0.5773502691896258
            const ly = -0.5773502691896258
            const lz = 0.5773502691896258

            const l =  0.8 + 0.2 * Math.max(0,nx*lx+ny*ly+nz*lz/Math.sqrt(nx*nx+ny*ny+nz*nz) )

            let xd = x + z * 0.7
            let yd = y + z3
            const off2 = (Math.round(Math.min(height - 1, Math.max(0,yd))) * width + Math.round(Math.min(width - 1, Math.max(0,xd)))) << 2

            const r = dataSrc[off2    ]
            const g = dataSrc[off2 + 1]
            const b = dataSrc[off2 + 2]

            dataDst[off    ] = r*l
            dataDst[off + 1] = g*l
            dataDst[off + 2] = b*l
            dataDst[off + 3] = 255

            off += 4
        }
    }
    ctx.putImageData(dst, 0, 0)
    return ctx.canvas
}


function main()
{
    const {width, height} = config

    random = new Prando(seed)
    noise = createNoise3D(() => random.next())

    const paint = (opts = {}) => {

        seed = opts.seed !== undefined ? opts.seed : randomSeed()

        console.log("Paint", opts)

        config.random = random

        const paletteRnd = random.next()
        if (opts.palette !== undefined)
        {
            paletteIndex = opts.palette
        }
        else
        {
            paletteIndex = 0 | allPalettesWithBlack.length * paletteRnd
        }
        const palette = allPalettesWithBlack[paletteIndex]
        ctx.fillStyle = palette[0 | random.next() * palette.length]
        ctx.fillRect(0, 0, width, height)

        const count = 3
        const marked = Math.floor(random.next() * count)
        for (let i = 0; i < count; i++)
        {
            const [colA, colB] = randomPair(palette)
            const fill = generateFill(colA, colB)

            tmpCtx.clearRect(0, 0, width, height)

            paintLayer(tmpCtx, i === marked)
            tmpCtx.fillStyle = fill
            tmpCtx.fill("evenodd")
            mark()

            //alphaDegrade(tmpCtx)

            ctx.drawImage(tmpCanvas, 0, 0)
        }

        applyPaper()

        // ctx.globalCompositeOperation = "destination-in"
        // ctx.drawImage(paper, 0, 0)
        // ctx.globalCompositeOperation = "source-over"

        // root.render(
        //     <UserInterface
        //         paletteIndex={ paletteIndex }
        //         paletteLocked={ paletteLocked }
        //         changePaletteLocked={  pl => {
        //
        //             console.log("CHANGE PALETTE LOCKED", pl)
        //             paletteLocked = pl
        //         }}
        //         changePalette={
        //             idx => {
        //                 console.log("CHANGE PALETTE", idx)
        //                 paint( { seed, palette: idx } )
        //             }
        //         }
        //         paint={ paint }
        //     />
        // )
    }

    root = createRoot(
        document.getElementById("react-root")
    )

    paint()

    canvas.addEventListener("click", () => {
        paint()
    }, true)
}

window.onload = (
    () => {

        canvas = document.getElementById("screen");
        ctx = canvas.getContext("2d");

        const width = (window.innerWidth) | 0;
        const height = (window.innerHeight) | 0;

        config.width = width;
        config.height = height;

        canvas.width = width;
        canvas.height = height;

        tmpCanvas = document.createElement("canvas")
        tmpCanvas.width = width
        tmpCanvas.height = height

        tmpCtx = tmpCanvas.getContext("2d")

        main()
    }
);
